import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Code,
  LogOut,
  Terminal,
  User,
  Clock,
  ArrowLeft,
  Activity,
  CheckCircle,
  TrendingUp,
  PlusCircle,
  HelpCircle,
  Award
} from 'react-feather';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from 'shared/utils/firebase';
import { signOut } from 'firebase/auth';
import styles from './ProfilePage.module.css';

const ProfilePage = ({ isAuthenticated, userDetails, theme, toggleTheme }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Firestore query states
  const [savedCodes, setSavedCodes] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [practiceProgress, setPracticeProgress] = useState([]);

  // Fetch all user specific data on auth trigger
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserData(user.uid);
      } else {
        // Redirect if not authenticated
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchUserData = async (uid) => {
    setIsLoading(true);
    try {
      // 1. Fetch saved codes (codeProjects)
      const codesQuery = query(
        collection(db, "codeProjects"),
        where("userId", "==", uid)
      );
      const codesSnap = await getDocs(codesQuery);
      const codesList = codesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort manually desc based on timestamp
      codesList.sort((a, b) => {
        const timeA = a.timestamp?.seconds || a.updatedAt?.seconds || 0;
        const timeB = b.timestamp?.seconds || b.updatedAt?.seconds || 0;
        return timeB - timeA;
      });
      setSavedCodes(codesList);

      // 2. Fetch coding test evaluations
      const testsQuery = query(
        collection(db, "testResults"),
        where("userId", "==", uid)
      );
      const testsSnap = await getDocs(testsQuery);
      const testsList = testsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort manually desc based on timestamp
      testsList.sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0;
        const timeB = b.timestamp?.seconds || 0;
        return timeB - timeA;
      });
      setTestResults(testsList);

      // 3. Fetch coding challenges list
      try {
        const challengesQuery = query(
          collection(db, "codeChallenges"),
          where("userId", "==", uid)
        );
        const challengesSnap = await getDocs(challengesQuery);
        setChallenges(challengesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.warn("Error fetching codeChallenges:", err);
        setChallenges([]);
      }

      // 4. Fetch coding practice modules
      try {
        const practiceQuery = query(
          collection(db, "codeProgress"),
          where("userId", "==", uid)
        );
        const practiceSnap = await getDocs(practiceQuery);
        setPracticeProgress(practiceSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.warn("Error fetching codeProgress:", err);
        setPracticeProgress([]);
      }

    } catch (error) {
      console.error("Error retrieving user profile statistics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleOpenWorkspace = () => {
    navigate('/ide');
  };

  const handleLoadProjectDirectly = (project) => {
    // Navigate to IDE and pass state containing the project object
    navigate('/ide', { state: { projectToLoad: project } });
  };

  // Calculations for coding analytics card summaries
  const statsOverview = useMemo(() => {
    const totalSaves = savedCodes.length;
    const totalTests = testResults.length;

    // Average score calculation
    let avgScore = 0;
    if (totalTests > 0) {
      const sum = testResults.reduce((acc, curr) => acc + (curr.score || 0), 0);
      avgScore = Math.round(sum / totalTests);
    }

    // Pass rate percentage
    let passRate = 0;
    if (totalTests > 0) {
      const passes = testResults.filter(t => t.passed || t.isPassed || (t.score >= 70)).length;
      passRate = Math.round((passes / totalTests) * 100);
    }

    return {
      totalSaves,
      totalTests,
      avgScore,
      passRate
    };
  }, [savedCodes, testResults]);

  // Formatter for timestamp values
  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000 || ts);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Build initials for avatar
  const avatarInitials = useMemo(() => {
    if (userDetails?.name) {
      return userDetails.name.charAt(0).toUpperCase();
    }
    return (userDetails?.email?.charAt(0) || 'U').toUpperCase();
  }, [userDetails]);

  return (
    <div className={`${styles.profileContainer} ${theme === 'light' ? styles.lightTheme : ''}`}>
      {/* Brand Header */}
      <header className={styles.header}>
        <div className={styles.logoContainer} onClick={() => navigate('/')} title="Back to home">
          <img src="/static_img/weblogo.png" alt="SiliconLogix Logo" className={styles.webLogo} />
        </div>
        <div className={styles.navActions}>
          <button className={styles.btnNav} onClick={() => navigate('/exercise')}>
            <Award size={14} />
            <span>Exercises</span>
          </button>
          <button className={styles.btnNav} onClick={handleOpenWorkspace}>
            <Code size={14} />
            <span>Open IDE</span>
          </button>
          <button className={styles.themeToggleBtn} onClick={toggleTheme} title="Switch theme">
            {theme === 'light' ? (
              /* Moon icon */
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            ) : (
              /* Sun icon */
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            )}
          </button>
          <button className={styles.btnNav} onClick={handleLogout}>
            <LogOut size={14} />
            <span>Log Out</span>
          </button>
        </div>
      </header>

      {/* Main Panel */}
      <main className={styles.container}>
        <div className={styles.profileContent}>

          {/* Back Navigation */}
          <button className={styles.btnNav} onClick={() => navigate(-1)} style={{ marginBottom: '24px' }}>
            <ArrowLeft size={13} />
            <span>Back</span>
          </button>

          {/* User Banner */}
          <div className={styles.userBanner}>
            <div className={styles.bannerAvatar}>
              {avatarInitials}
            </div>
            <div className={styles.bannerInfo}>
              <h1 className={styles.userName}>
                {userDetails?.name || `${userDetails?.first_name || ''} ${userDetails?.last_name || ''}`.trim() || 'Silicon Logix User'}
              </h1>
              <p className={styles.userEmail}>{userDetails?.email || 'N/A'}</p>
              <div className={styles.badgeRow}>
                {userDetails?.siliconId && (
                  <span className={styles.statusBadge} style={{ fontFamily: 'monospace', color: '#38bdf8', borderColor: 'rgba(56, 189, 248, 0.4)' }}>
                    ⚡ {userDetails.siliconId}
                  </span>
                )}
                <span className={styles.roleBadge}>{userDetails?.role || 'Student'}</span>
                <span className={styles.statusBadge}>Active Profile</span>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className={styles.loadingBox}>
              Fetching secure coding profile data...
            </div>
          ) : (
            <>
              {/* Analytics summary row */}
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <Code size={20} />
                  </div>
                  <div className={styles.statMeta}>
                    <span className={styles.statValue}>{statsOverview.totalSaves}</span>
                    <span className={styles.statLabel}>Saved Code Files</span>
                  </div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <Terminal size={20} />
                  </div>
                  <div className={styles.statMeta}>
                    <span className={styles.statValue}>{statsOverview.totalTests}</span>
                    <span className={styles.statLabel}>Test Evaluations</span>
                  </div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <CheckCircle size={20} />
                  </div>
                  <div className={styles.statMeta}>
                    <span className={styles.statValue}>{statsOverview.passRate}%</span>
                    <span className={styles.statLabel}>Coding Test Pass Rate</span>
                  </div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <TrendingUp size={20} />
                  </div>
                  <div className={styles.statMeta}>
                    <span className={styles.statValue}>{statsOverview.avgScore}/100</span>
                    <span className={styles.statLabel}>Avg Coding Score</span>
                  </div>
                </div>
              </div>

              {/* Tab Pills */}
              <div className={styles.tabSection}>
                <button
                  className={`${styles.tabPill} ${activeTab === 'overview' ? styles.activeTabPill : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  <Activity size={14} />
                  <span>Overview</span>
                </button>
                <button
                  className={`${styles.tabPill} ${activeTab === 'saves' ? styles.activeTabPill : ''}`}
                  onClick={() => setActiveTab('saves')}
                >
                  <Code size={14} />
                  <span>Saved Code Gallery</span>
                </button>
                <button
                  className={`${styles.tabPill} ${activeTab === 'tests' ? styles.activeTabPill : ''}`}
                  onClick={() => setActiveTab('tests')}
                >
                  <Terminal size={14} />
                  <span>Coding Tests</span>
                </button>
                <button
                  className={`${styles.tabPill} ${activeTab === 'challenges' ? styles.activeTabPill : ''}`}
                  onClick={() => setActiveTab('challenges')}
                >
                  <Award size={14} />
                  <span>Challenges & Practice</span>
                </button>
              </div>

              {/* Tab Contents */}
              <div className={styles.tabContent}>

                {/* 1. Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '16px' }}>Recent Simulation & Practice Summary</h3>
                    {testResults.length === 0 && savedCodes.length === 0 ? (
                      <div className={styles.placeholderCard}>
                        <HelpCircle size={24} className={styles.placeholderIcon} />
                        <p className={styles.placeholderTitle}>No Coding Data Found</p>
                        <p className={styles.placeholderDesc}>Start writing code models and executing testbenches inside the IDE playground to populate your history.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {savedCodes.length > 0 && (
                          <div style={{ padding: '16px', background: 'var(--profile-surface-card)', borderRadius: '8px', border: '1px solid var(--profile-border)' }}>
                            <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 10px 0', color: 'var(--profile-accent)' }}>Last Active Project Workspace</p>
                            <p style={{ fontSize: '15px', fontWeight: 'bold', margin: '0 0 6px 0' }}>{savedCodes[0].title || 'Untitled Project'}</p>
                            <p style={{ fontSize: '11px', color: 'var(--profile-text-muted)', margin: '0 0 12px 0' }}>Last Updated: {formatTimestamp(savedCodes[0].timestamp || savedCodes[0].updatedAt)}</p>
                            <button className={styles.btnOpenCode} style={{ maxWidth: '200px' }} onClick={() => handleLoadProjectDirectly(savedCodes[0])}>
                              Resume Coding Workspace
                            </button>
                          </div>
                        )}

                        {testResults.length > 0 && (
                          <div>
                            <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 10px 0' }}>Last Evaluation</p>
                            <div className={styles.historyItem}>
                              <div className={styles.historyLeft}>
                                <Terminal size={16} />
                                <div className={styles.historyDetails}>
                                  <p className={styles.historyTitle}>{testResults[0].category || 'Coding Test Execution'}</p>
                                  <p className={styles.historyTime}>{formatTimestamp(testResults[0].timestamp)}</p>
                                </div>
                              </div>
                              <div className={styles.historyRight}>
                                <span className={styles.historyScore}>Score: {testResults[0].score || 0}/100</span>
                                <span className={`${styles.statusIndicator} ${testResults[0].passed || testResults[0].isPassed || (testResults[0].score >= 70) ? styles.statusPassed : styles.statusFailed}`}>
                                  {testResults[0].passed || testResults[0].isPassed || (testResults[0].score >= 70) ? 'Passed' : 'Failed'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* 2. Saves Tab */}
                {activeTab === 'saves' && (
                  <div>
                    {savedCodes.length === 0 ? (
                      <div className={styles.placeholderCard}>
                        <Code size={24} className={styles.placeholderIcon} />
                        <p className={styles.placeholderTitle}>No Saved Code Files</p>
                        <p className={styles.placeholderDesc}>You haven't saved any workspaces in this profile yet. Open the IDE, write some code, and click Save Project.</p>
                        <button className={styles.btnOpenCode} style={{ maxWidth: '180px', marginTop: '10px' }} onClick={handleOpenWorkspace}>
                          Open IDE Workspace
                        </button>
                      </div>
                    ) : (
                      <div className={styles.savesGrid}>
                        {savedCodes.map(save => (
                          <div key={save.id} className={styles.saveCard}>
                            <div className={styles.saveHeader}>
                              <h4 className={styles.saveTitle}>{save.title || 'Untitled Project'}</h4>
                              <span className={styles.langPill}>{save.primaryLanguage || 'Verilog'}</span>
                            </div>
                            <div className={styles.saveMeta}>
                              <Clock size={11} />
                              <span>{formatTimestamp(save.timestamp || save.updatedAt)}</span>
                            </div>
                            <button className={styles.btnOpenCode} onClick={() => handleLoadProjectDirectly(save)}>
                              Open in Workspace
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 3. Tests Tab */}
                {activeTab === 'tests' && (
                  <div>
                    {testResults.length === 0 ? (
                      <div className={styles.placeholderCard}>
                        <Terminal size={24} className={styles.placeholderIcon} />
                        <p className={styles.placeholderTitle}>No Coding Tests Recorded</p>
                        <p className={styles.placeholderDesc}>Coding evaluation runs will be tracked here once you execute test checks in practice modules.</p>
                      </div>
                    ) : (
                      <div className={styles.testHistoryList}>
                        {testResults.map(test => (
                          <div key={test.id} className={styles.historyItem}>
                            <div className={styles.historyLeft}>
                              <Terminal size={16} />
                              <div className={styles.historyDetails}>
                                <p className={styles.historyTitle}>{test.category || 'Simulation Testbench Run'}</p>
                                <p className={styles.historyTime}>{formatTimestamp(test.timestamp)}</p>
                              </div>
                            </div>
                            <div className={styles.historyRight}>
                              <span className={styles.historyScore}>Score: {test.score || 0}/100</span>
                              <span className={`${styles.statusIndicator} ${test.passed || test.isPassed || (test.score >= 70) ? styles.statusPassed : styles.statusFailed}`}>
                                {test.passed || test.isPassed || (test.score >= 70) ? 'Passed' : 'Failed'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 4. Challenges & Practice Tab (Future Proofed) */}
                {activeTab === 'challenges' && (
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '16px' }}>Solved Coding Challenges & Badges</h3>

                    {challenges.length === 0 && practiceProgress.length === 0 ? (
                      <div className={styles.placeholderCard}>
                        <Award size={24} className={styles.placeholderIcon} />
                        <p className={styles.placeholderTitle}>Coding Challenges Coming Soon</p>
                        <p className={styles.placeholderDesc}>Interactive coding challenges, grading metrics, and practice achievements progress will be displayed here in the next platform release.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {challenges.map(c => (
                          <div key={c.id} className={styles.historyItem}>
                            <div className={styles.historyLeft}>
                              <Award size={16} />
                              <div className={styles.historyDetails}>
                                <p className={styles.historyTitle}>{c.challengeTitle || 'Hardware Challenge'}</p>
                                <p className={styles.historyTime}>Solved: {formatTimestamp(c.lastAttempted)}</p>
                              </div>
                            </div>
                            <div className={styles.historyRight}>
                              <span className={styles.historyScore}>Rank: #{c.score}</span>
                              <span className={`${styles.statusIndicator} ${styles.statusPassed}`}>Solved</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </>
          )}

        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
